import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Package, Pencil, Plus, Trash2 } from "lucide-react";
import type { JSX } from "react";

import { type Product, type ProductFeature, getProduct } from "../products-api.js";
import { FeatureRow } from "./FeatureRow.js";
import { featureGridTemplate } from "./featureColumns.js";
import { pricingFeatureSections } from "./featureSections.js";

/**
 * The column headings over a product's features. Same template as the rows
 * (`featureColumns.ts`), so "Part number" and "Category" stay above the things
 * they name.
 *
 * The name cell is inset by the width of a row's branch icon plus its gap —
 * 14px + 8px — because the heading labels the NAMES, not the icons.
 */
function FeatureHeader(props: { canEdit: boolean }): JSX.Element {
  const cell = { fontSize: 11, fontWeight: 600, letterSpacing: 0.4, color: "text.disabled" } as const;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: featureGridTemplate(props.canEdit),
        alignItems: "center",
        gap: 1.5,
        px: 1,
        pb: 0.5,
      }}
      data-testid="feature-columns-header"
    >
      <Typography component="span" sx={{ ...cell, pl: "22px" }}>
        Feature
      </Typography>
      <Typography component="span" sx={cell}>
        Part number
      </Typography>
      <Typography component="span" sx={cell}>
        Category
      </Typography>
      <Typography component="span" sx={{ ...cell, textAlign: "right" }}>
        Price
      </Typography>
      {props.canEdit && <Box />}
    </Box>
  );
}

/**
 * One product row. Owns the lazy per-product detail query
 * (`["admin","product",id]`, enabled only when expanded) that hydrates the
 * feature list; renders a `FeatureHeader` and then a `FeatureRow` per feature —
 * part number, category and price as aligned columns.
 *
 * Two levels, and only two: the catalogue lost its version level on
 * 2026-09-13. The features sit under SECTION headings (2026-09-15) — Value
 * Added Services, Platform Extensions, then any other category — the same two
 * headings the licence builder and the agreement modal use, so a feature is
 * found in the same place wherever it is met (`featureSections.ts`).
 */
export function ProductRow(props: {
  product: Product;
  expanded: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onAddFeature: () => void;
  onEditFeature: (f: ProductFeature) => void;
  onDeleteFeature: (f: ProductFeature) => void;
}): JSX.Element {
  const { product, expanded, canEdit } = props;
  const detail = useQuery({
    queryKey: ["admin", "product", product.id],
    queryFn: () => getProduct(product.id),
    enabled: expanded,
  });
  const features = detail.data?.features ?? product.features ?? [];

  return (
    <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1.5, py: 1 }}>
        <Box
          component="button"
          type="button"
          data-testid={`product-row-${product.id}`}
          onClick={props.onToggle}
          sx={{
            display: "flex",
            flex: 1,
            alignItems: "center",
            gap: 1,
            textAlign: "left",
            border: 0,
            bgcolor: "transparent",
            p: 0,
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            minWidth: 0,
          }}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <Box component="span" sx={{ display: "inline-flex", color: "primary.main" }}>
            <Package size={16} />
          </Box>
          <Box component="span" sx={{ fontWeight: 500 }}>{product.name}</Box>
          {product.code && (
            <Box component="span" sx={{ borderRadius: 0.5, bgcolor: "surfaceSoft", px: 0.75, py: 0.25, fontSize: 12 }}>
              {product.code}
            </Box>
          )}
          <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
            {product.features_count ?? features.length} feature(s)
          </Box>
        </Box>
        {canEdit && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton size="small" color="inherit" aria-label={`Add feature to ${product.name}`} onClick={props.onAddFeature}>
              <Plus size={16} />
            </IconButton>
            <IconButton size="small" color="inherit" aria-label={`Edit ${product.name}`} onClick={props.onEdit}>
              <Pencil size={16} />
            </IconButton>
            <IconButton size="small" aria-label={`Delete ${product.name}`} onClick={props.onDelete} sx={{ color: "error.main" }}>
              <Trash2 size={16} />
            </IconButton>
          </Box>
        )}
      </Box>

      {expanded && (
        <Box sx={{ borderTop: 1, borderColor: "divider", px: 1.5, py: 1 }}>
          {detail.isLoading ? (
            <Skeleton variant="rounded" sx={{ height: 24, width: "66%" }} />
          ) : features.length === 0 ? (
            <Typography sx={{ py: 1, fontSize: 12, color: "text.disabled" }}>
              No features yet.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", gap: 0.5 }}>
              <FeatureHeader canEdit={canEdit} />
              {pricingFeatureSections(features).map((section) => (
                <Box key={section.key} data-testid={`feature-section-${section.key}`} sx={{ display: "grid", gap: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, pt: 1, pb: 0.25 }}>
                    <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary" }}>
                      {section.label}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
                      {section.features.length}
                    </Typography>
                  </Box>
                  {section.features.map((f) => (
                    <FeatureRow
                      key={f.id}
                      feature={f}
                      canEdit={canEdit}
                      onEdit={() => props.onEditFeature(f)}
                      onDelete={() => props.onDeleteFeature(f)}
                    />
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
